import { useMemo, FC } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { StatusCell } from 'components/Core/StatusCell';
import { IProductCategoryReqData } from 'stores/categories/types';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { productCategoriesTableTranslations } from './translations';
import { ProductCategoriesTableTransFields } from './types';
import styles from './productCategoriesTable.module.scss';

export const ProductCategoriesTable: FC = () => {
  const {
    categoriesStore: { productCategories, changeProductCategoryStatus },
  } = useStore();

  const { t } = useMyTranslations<ProductCategoriesTableTransFields>([productCategoriesTableTranslations.name]);

  const navigate = useNavigate();

  const columns: Column<IProductCategoryReqData>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { id, categoryName },
          },
        }: CellProps<IProductCategoryReqData>) => (
          <p className={styles.name} onClick={() => navigate(`${id}/basic`)}>
            {categoryName}
          </p>
        ),
        accessor: ({ categoryName }) => categoryName,
        width: 3,
      },
      {
        Header: t('keywords'),
        id: 'keywords',
        Cell: ({
          row: {
            original: { keywords },
          },
        }: CellProps<IProductCategoryReqData>) =>
          keywords && keywords.length > 0 ? (
            <div className={styles.chips}>
              {keywords.map((kw, i) => (
                <span key={`${kw}-${i}`} className={styles.chip}>
                  {kw}
                </span>
              ))}
            </div>
          ) : (
            <span className={styles.empty}>—</span>
          ),
        accessor: ({ keywords }) => (keywords || []).join(', '),
        width: 5,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IProductCategoryReqData>) => (
          <StatusCell id={id} status={status} changeStatus={changeProductCategoryStatus} />
        ),
        accessor: ({ status }) => status,
        width: 1,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({ row: { original } }: CellProps<IProductCategoryReqData>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button className={styles.editBtn} onClick={() => navigate(`${original.id}/basic`)} disabled={false}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message='edit'
              place='top'
            />
          </div>
        ),
        width: 1,
        disableSortBy: true,
      },
    ],
    [productCategories]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return productCategories && productCategories.length > 0 ? (
    <Table
      columns={columns as never}
      data={[...productCategories]}
      simpleList
      isPaginated={productCategories.length > PAGE_SIZE}
    />
  ) : (
    <NoDataComponent />
  );
};

export default observer(ProductCategoriesTable);
